import {
  Text,
  useToast,
  Divider,
  Flex,
  Box,
  Button,
  useDisclosure,
  HStack,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';
import { commonFormulas } from '@finley-technologies/formula-engine';

import { DeleteConfirmationModal } from '../../../delete/DeleteArrayPropertyButton';
import { PreviewPostgresQueryButton } from '../PreviewPostgresQueryButton';
import { PostgresQueryDateFormulaField } from './LenderPostgresQueryComponents';
import { QueryInputTypeBadge } from './QueryInputTypeBadge';
import { colors } from 'packages/common-frontend/colors';
import {
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';

const capitalProviderFieldPlaceholder =
  'All configured capital providers have already been added';
const placeholderSelectOptions = [
  {
    value: capitalProviderFieldPlaceholder,
    label: capitalProviderFieldPlaceholder,
  },
];

type CapitalProviderSlugsEditProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  principalBalanceDailyAverageQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery;
};
const CapitalProviderSlugsField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  principalBalanceDailyAverageQuery,
}: CapitalProviderSlugsEditProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialCapitalProviderSlugs =
    principalBalanceDailyAverageQuery.parameterConfig.capitalProviderSlugs
      .slugs;

  const [latestCapitalProviderSlugs, setLatestCapitalProviderSlugs] = useState<
    string[]
  >(initialCapitalProviderSlugs);
  const onAddCapitalProviderSlug = useCallback(
    ({ newCapitalProviderSlug }: { newCapitalProviderSlug: string }) => {
      const fieldsStateCopy = _.cloneDeep(latestCapitalProviderSlugs);
      fieldsStateCopy.push(newCapitalProviderSlug);
      setLatestCapitalProviderSlugs(fieldsStateCopy);
    },
    [latestCapitalProviderSlugs],
  );
  const onDeleteCapitalProviderSlug = useCallback(
    ({
      capitalProviderSlugToDelete,
    }: {
      capitalProviderSlugToDelete: string;
    }) => {
      const newFieldsWithoutFieldToDelete = _.without(
        latestCapitalProviderSlugs,
        capitalProviderSlugToDelete,
      );
      setLatestCapitalProviderSlugs(newFieldsWithoutFieldToDelete);
    },
    [latestCapitalProviderSlugs],
  );

  const selectOptions:
    | {
        value: string;
        label: string;
      }[]
    | null = useMemo(() => {
    if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
      const facilityMechanics: CreditAgreement.DomainEntity.FacilityMechanicsWithEntities = getIn(
        wipConfig.formProps.values,
        'facilityMechanics',
      );

      const allCapitalProviderSlugs = _.map(
        facilityMechanics.parties.capitalProviders,
        (capitalProvider) => capitalProvider.entity.slug,
      );

      return _.compact(
        _.map(allCapitalProviderSlugs, (capitalProviderSlug) => {
          if (!_.includes(latestCapitalProviderSlugs, capitalProviderSlug)) {
            return {
              value: capitalProviderSlug,
              label: capitalProviderSlug,
            };
          }
          return null;
        }),
      );
    }
    return null;
  }, [wipConfig, fullPropertyKey, latestCapitalProviderSlugs]);

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          <Text color={colors.black[70]} mb="24px">
            You can add multiple options, one at a time.
          </Text>
          <Flex alignItems="center" justifyContent="space-between">
            <Select
              value=""
              placeholderLabel={
                selectOptions && selectOptions.length
                  ? 'Select'
                  : capitalProviderFieldPlaceholder
              }
              options={
                selectOptions && selectOptions.length
                  ? selectOptions
                  : placeholderSelectOptions
              }
              isDisabled={!selectOptions || !selectOptions.length}
              onChange={(value) => {
                if (value) {
                  onAddCapitalProviderSlug({
                    newCapitalProviderSlug: value,
                  });
                }
              }}
              width="100%"
            />
            <>
              <Button
                color={colors.aurora[400]}
                border="2px"
                borderRadius="8px"
                borderColor={colors.aurora[400]}
                backgroundColor="white"
                onClick={() => {
                  setLatestCapitalProviderSlugs(initialCapitalProviderSlugs);
                  onEditClose();
                }}
                ml="32px"
                mr="24px"
              >
                Cancel
              </Button>
              <Button
                color="white"
                backgroundColor={colors.aurora[400]}
                isLoading={isSaveLoading}
                onClick={async () => {
                  try {
                    const updatedQuery = _.cloneDeep(
                      principalBalanceDailyAverageQuery,
                    );
                    updatedQuery.parameterConfig.capitalProviderSlugs.slugs = latestCapitalProviderSlugs;

                    await onSave(updatedQuery);
                  } catch (err) {
                    toast({
                      ...errorToastConfig,
                      id: 'saveError',
                      title: 'Error saving formula. Please try again.',
                    });
                  } finally {
                    onEditClose();
                  }
                }}
                isDisabled={!latestCapitalProviderSlugs.length}
              >
                Save
              </Button>
            </>
          </Flex>
          <HStack alignItems="center" mt="24px">
            {_.map(latestCapitalProviderSlugs, (latestStateField) => (
              <Flex
                key={`capital-provider-button-${latestStateField}`}
                alignItems="center"
                color={colors.black[70]}
                cursor="default"
                mr="24px"
              >
                <Text fontWeight={400} mr="10px">
                  {latestStateField}
                </Text>
                <Tooltip label={`Delete ${latestStateField}`}>
                  <Flex
                    cursor="pointer"
                    onClick={() =>
                      onDeleteCapitalProviderSlug({
                        capitalProviderSlugToDelete: latestStateField,
                      })
                    }
                  >
                    <Icon icon="x" size="12px" />
                  </Flex>
                </Tooltip>
              </Flex>
            ))}
          </HStack>
          {!latestCapitalProviderSlugs.length && (
            <Text color={colors.black[70]}>
              Must have at least one capital provider selected.
            </Text>
          )}
        </>
      ) : (
        <Flex justifyContent="space-between">
          <VStack spacing="16px" alignItems="flex-start" alignSelf="center">
            {_.map(
              initialCapitalProviderSlugs,
              (existingCapitalProviderSlug) => (
                <Text
                  key={existingCapitalProviderSlug}
                  color={colors.black[70]}
                  textAlign="left"
                >
                  {existingCapitalProviderSlug}
                </Text>
              ),
            )}
          </VStack>
          <EditIconButton
            label="Edit Capital Providers"
            ariaLabel="edit capital providers"
            onClick={onEditOpen}
          />
        </Flex>
      )}
    </Box>
  );
};

type LenderPrincipalBalanceDailyAverageViewProps = {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  triggerDate?: Util.FullDateStr;
};
export const LenderPrincipalBalanceDailyAverageView = ({
  postgresQuery,
  wipConfig,
  fullPropertyKey,
  duplicateParams,
  deleteParams,
  onSave,
  isSaveLoading,
  presetVariables,
  inputDateType,
  triggerDate,
}: LenderPrincipalBalanceDailyAverageViewProps) => {
  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;

  const evaluatedDates = useMemo(() => {
    if (!triggerDate) {
      return null;
    }
    return commonFormulas.evaluateStartAndEndDateFormulas({
      startFormula: postgresQuery.parameterConfig.startDateFormula.formula,
      endFormula: postgresQuery.parameterConfig.endDateFormula.formula,
      variables: [
        { name: 'triggerDate', value: triggerDate, parseNumber: true },
        ...postgresQuery.variables,
      ],
    });
  }, [postgresQuery, triggerDate]);

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="principalBalanceDailyAverage"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="principalBalanceDailyAverage" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {postgresQuery.name}
      </Text>

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Starting Date
      </Text>
      <PostgresQueryDateFormulaField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        postgresQuery={postgresQuery}
        formulaFieldKey="startDateFormula"
        evaluatedDate={evaluatedDates?.startDate}
        triggerDate={triggerDate}
      />
      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Ending Date
      </Text>
      <PostgresQueryDateFormulaField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        postgresQuery={postgresQuery}
        formulaFieldKey="endDateFormula"
        evaluatedDate={evaluatedDates?.endDate}
        triggerDate={triggerDate}
      />

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Capital Providers
      </Text>
      <CapitalProviderSlugsField
        fullPropertyKey={fullPropertyKey}
        wipConfig={wipConfig}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        principalBalanceDailyAverageQuery={postgresQuery}
      />

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <PreviewPostgresQueryButton
          wipConfig={wipConfig}
          postgresQuery={postgresQuery}
          inputDateType={inputDateType}
          presetVariables={presetVariables}
          queryType={postgresQuery.type}
          startDate={evaluatedDates?.startDate}
          endDate={evaluatedDates?.endDate}
          selectedInputDate={triggerDate}
        />
      )}
    </Box>
  );
};
